/**
 * O único lugar do projeto que sabe onde a sessão é guardada.
 *
 * Até aqui, `localStorage.getItem('token')` aparecia literal em treze arquivos — componentes,
 * páginas, guardas de rota e o interceptor —, e a chave `'token'` era digitada à mão em cada um.
 * Isso tem dois custos. O imediato: trocar o backend de armazenamento significa dezenove edições.
 * O que vem depois: no app nativo o localStorage do WKWebView pode ser despejado sob pressão de
 * armazenamento, e a durabilidade precisa vir do @capacitor/preferences — que é assíncrono. Com a
 * leitura espalhada, não há onde encaixar a hidratação.
 *
 * <h2>Por que continua síncrono</h2>
 * `PrivateRoute` (src/App.jsx) lê a sessão DURANTE o render, para decidir entre renderizar a rota e
 * redirecionar para /login. Um `await` ali significaria um estado de carregamento em toda rota
 * protegida, e um piscar de tela a cada navegação. Então o localStorage segue como fonte de verdade
 * e a API daqui é síncrona de propósito: quando o Preferences entrar, ele hidrata o localStorage no
 * boot, ANTES do createRoot, e espelha as escritas — sem que nenhum dos chamadores mude.
 *
 * <h2>O try/catch</h2>
 * Não é zelo excessivo: navegador em modo privado, com dados de site bloqueados, ou uma WebView com
 * armazenamento desabilitado fazem o próprio acessador lançar — não devolver vazio. Sem guarda, isso
 * derruba o render de qualquer tela que pergunte se há sessão. Falhar como "não há sessão" é o pior
 * caso aceitável; derrubar a página não é.
 */

// As chaves são literais aqui e em lugar nenhum mais.
const CHAVE_TOKEN = 'token';
const CHAVE_REFRESH = 'refreshToken';
const CHAVE_PUSH = 'aparelhoPush';

/** O JWT da sessão, ou null quando não há (ou quando o armazenamento não pode ser lido). */
export function lerToken() {
	try {
		return localStorage.getItem(CHAVE_TOKEN);
	} catch {
		return null;
	}
}

/**
 * O refresh token, ou null.
 *
 * Nulo é um estado NORMAL, não um erro: quem entrou antes de o refresh existir tem só o JWT
 * guardado, e é essa leitura devolvendo null que faz o services/api cair no comportamento antigo em
 * vez de tentar renovar uma sessão que nunca teve como renovar.
 */
export function lerRefreshToken() {
	try {
		return localStorage.getItem(CHAVE_REFRESH);
	} catch {
		return null;
	}
}

/**
 * Guarda o par recebido do login ou de uma renovação.
 *
 * Recebe o corpo da resposta inteiro, e não dois argumentos, porque é exatamente o formato que
 * `/login` e `/login/refresh` devolvem -- passar `dados` evita que um dos dois chamadores inverta a
 * ordem dos parâmetros um dia.
 *
 * `refreshToken` ausente não apaga o que já existe: o backend só passou a devolvê-lo na v1.11.3, e
 * uma resposta antiga (de um backend não atualizado) não pode derrubar a renovação de quem já tem.
 *
 * @param {{ token: string, refreshToken?: string }} dados
 */
export function salvarSessao(dados) {
	try {
		if (dados?.token) localStorage.setItem(CHAVE_TOKEN, dados.token);
		if (dados?.refreshToken) localStorage.setItem(CHAVE_REFRESH, dados.refreshToken);
	} catch {
		// Sem armazenamento, a sessão vale enquanto a aba viver. Não há nada melhor a fazer aqui, e
		// estourar impediria o login de concluir numa situação que o usuário não escolheu.
	}
}

/**
 * Guarda a sessão de um LOGIN (não de uma renovação, que continua no `salvarSessao`) e esquece a
 * confirmação do aparelho de push.
 *
 * O `confirmarToken` (services/pushNotificacoes.js) pula o envio do token por 12 h quando ele já foi
 * confirmado. Só que o backend apaga os aparelhos da pessoa em TODO logout, de qualquer lugar,
 * inclusive do site (RefreshTokenService). Depois disso o app cai no login com o mesmo token guardado
 * como "confirmado", e não o reenvia: até 12 h sem aparelho no backend, com tudo saindo por SMS. Foi o
 * que aconteceu em 2026-10-09 (prompts/push-reconfirma-apos-login.md). Sessão nova, confirmação nova:
 * o próximo `register()` manda o token, e o backend recria a linha ou troca o dono.
 *
 * @param {{ token: string, refreshToken?: string }} dados
 */
export function iniciarSessao(dados) {
	salvarSessao(dados);
	limparAparelhoPush();
}

/**
 * Encerra a sessão local — os dois tokens.
 *
 * Só o lado do cliente. Revogar o refresh no servidor é outra chamada, em services/autenticacao.js,
 * e ela existe porque apagar daqui não invalida nada: o refresh continuaria valendo por 30 dias
 * para quem tivesse uma cópia.
 */
export function limparSessao() {
	try {
		localStorage.removeItem(CHAVE_TOKEN);
		localStorage.removeItem(CHAVE_REFRESH);
	} catch {
		// idem
	}
}

/** Atalho de legibilidade para os lugares que só querem saber "tem alguém logado?". */
export function temSessao() {
	return Boolean(lerToken());
}

/**
 * O aparelho de push deste celular: o token do Firebase e quando ele foi confirmado ao backend pela
 * última vez. Só existe no app (services/pushNotificacoes.js).
 *
 * Fica FORA do `limparSessao` de propósito: quem apaga é o `encerrarPush`, que também desregistra o
 * token no Firebase. Se a sessão só expirar, o token continua no Firebase, e o próximo login (até de
 * outra conta) o reaproveita — o `iniciarSessao` apaga esta confirmação, o token é reenviado e o
 * backend troca o dono.
 *
 * @returns {{ token: string, confirmadoEm: number } | null}
 */
export function lerAparelhoPush() {
	try {
		const bruto = localStorage.getItem(CHAVE_PUSH);
		return bruto ? JSON.parse(bruto) : null;
	} catch {
		return null;
	}
}

export function salvarAparelhoPush(token) {
	try {
		localStorage.setItem(CHAVE_PUSH, JSON.stringify({ token, confirmadoEm: Date.now() }));
	} catch {
		// Sem armazenamento, o app só reconfirma o token com mais frequência.
	}
}

export function limparAparelhoPush() {
	try {
		localStorage.removeItem(CHAVE_PUSH);
	} catch {
		// idem
	}
}
